/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-kitchen.js · THE CEO KITCHEN (stage 3 wow builder, 09-10-2026)
   Behaviour of sections/ce-home-kitchen.liquid (looks: assets/ce-kitchen.css; notes: docs/notes/stage3-wow.md).

   - ONE IntersectionObserver for every kitchen on the page. Nothing runs until the drawing is in view.
   - The show: .is-armed (set by the section's inline script, so nothing flashes) -> .is-live + .is-drawing
     (CSS draws every line with its appliance's own delay, then the fills, then each appliance comes alive)
     -> .is-spots (the markers pop in) -> .is-done (the draw-in animations are removed; the few idle loops stay).
   - Markers: hover (mouse) previews a card, a click / tap / Enter pins it, keyboard focus previews it,
     Esc / outside click / the X closes it. Hovering or clicking an appliance in the drawing does the same as its
     marker. The appliance lights up (blue lines, the rest steps back) and plays its own little "hello".
   - One marker at a time sends out a slow ping while nothing is open (stops off screen / hidden tab).
   - Reduced motion, no IntersectionObserver, the theme editor: the finished picture at once, markers working.
   No libraries. No layout reads in a loop; the card is measured once when it opens.
   ========================================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.CEKitchen) return;

  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const sheet = W.matchMedia('(max-width: 989px)');   // the swipe row + the bottom-sheet card (ce-kitchen.css 10)
  const editor = !!(W.Shopify && W.Shopify.designMode);
  const idle = W.requestIdleCallback ? (f) => W.requestIdleCallback(f, { timeout: 1500 }) : (f) => setTimeout(f, 250);
  const kitchens = new Set();
  const byFrame = new WeakMap();
  const CALM_MS = 14000;              // how long the idle loops run after the show / after the last nudge

  const io = ('IntersectionObserver' in W) ? new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      const k = byFrame.get(en.target);
      if (!k) return;
      k.seen = en.isIntersecting;
      k.el.classList.toggle('is-off', !en.isIntersecting);
      if (en.isIntersecting && en.intersectionRatio >= 0.28) k.start();
      if (!en.isIntersecting && sheet.matches) k.hide();
      k.pingSync();
    });
  }, { threshold: [0, 0.28, 0.6] }) : null;

  class Kitchen {
    constructor(el) {
      this.el = el;
      this.frame = el.querySelector('.cek__frame');
      this.art = el.querySelector('.cek-art');
      this.stage = el.querySelector('.cek__stage') || el;
      this.spots = Array.prototype.map.call(el.querySelectorAll('.cek-spot'), (sp, i) => ({
        el: sp, i, app: sp.getAttribute('data-app') || '',
        btn: sp.querySelector('.cek-spot__btn'), card: sp.querySelector('.cek-card'), x: sp.querySelector('.cek-card__close')
      })).filter((s) => s.btn && s.card);
      this.byApp = new Map();
      this.spots.forEach((s) => { if (s.app && !this.byApp.has(s.app)) this.byApp.set(s.app, s); });
      this.open = null;
      this.pinned = false;
      this.started = false;
      this.seen = false;
      this.ping = 0;
      this.pingAt = -1;
      this.timers = [];
      this.helloAt = {};
      this.muting = false;
      if (this.art) {
        this.art.querySelectorAll('.cek-app[data-app]').forEach((g) => {
          if (this.byApp.has(g.getAttribute('data-app'))) g.setAttribute('data-spot', '');
        });
      }
      this.bind();
      this.shortList();
      el.classList.add('is-booted');
      // not armed (reduced motion, theme editor, or the failsafe already showed the picture): the still picture
      if (!io || !el.classList.contains('is-armed') || reduce.matches || editor) this.finish();
      if (io && this.frame) { byFrame.set(this.frame, this); io.observe(this.frame); }
    }

    later(fn, ms) { this.timers.push(setTimeout(fn, ms)); }

    start() {
      if (this.started) return;
      this.started = true;
      const el = this.el;
      el.classList.remove('is-armed');
      el.classList.add('is-live', 'is-drawing');
      this.later(() => el.classList.add('is-spots'), 2650);
      this.pan();
      // the finished kitchen catches the light once, just as it settles (a permanent element: a class, no insertion)
      this.later(() => { const g = this.stage.querySelector('.cek__glint'); if (g && this.seen) g.classList.add('is-run'); }, 3350);
      // the draw-in is over when the last glint has played (or after 8 s, whatever happened meanwhile)
      const glints = this.art ? this.art.querySelectorAll('.hl') : [];
      const last = glints[glints.length - 1];
      const done = () => {
        if (el.classList.contains('is-done')) return;
        el.classList.remove('is-drawing');
        el.classList.add('is-done', 'is-spots');
        this.wake(CALM_MS);
        this.pingSync();
        this.preload();
      };
      if (last) last.addEventListener('animationend', done, { once: true });
      this.later(done, 8000);
    }

    // phones / tablets: follow the drawing as it sweeps across the kitchen, hold, then glide back to the start.
    // Any touch, wheel or key on the kitchen stops it at once.
    pan() {
      const sc = this.frame && this.frame.querySelector('.cek__scroll');
      if (!sc || !sheet.matches || reduce.matches) return;
      const vw = sc.clientWidth, max = sc.scrollWidth - vw, wide = this.stage.offsetWidth;
      if (max < 40) return;
      let stop = false;
      const end = () => { stop = true; this.el.classList.remove('is-panning'); };
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((ev) => sc.addEventListener(ev, end, { once: true, passive: true }));
      this.el.classList.add('is-panning');
      const ease = (p) => (p < .5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
      const front = (t) => Math.min(max, Math.max(0, ((t - 0.25) / 1.85) * wide - 0.72 * vw));  // keeps the drawing wave in view
      const HOLD_END = 3900, BACK = 1500;
      const t0 = performance.now();
      const step = (now) => {
        if (stop || !this.el.isConnected) return;
        const e = now - t0;
        if (e < HOLD_END) sc.scrollLeft = front(e / 1000);
        else if (e < HOLD_END + BACK) sc.scrollLeft = max * (1 - ease((e - HOLD_END) / BACK));
        else { sc.scrollLeft = 0; end(); return; }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    finish() {
      this.started = true;
      this.timers.forEach(clearTimeout);
      this.el.classList.remove('is-armed', 'is-drawing', 'is-live');
      this.el.classList.add('is-done');
      this.preload();
      this.pingSync();
    }

    // the idle loops run while someone is engaged with the kitchen, then settle (CSS .is-calm pauses them)
    wake(ms) {
      if (!this.el.classList.contains('is-live')) return;
      clearTimeout(this.calmT);
      this.el.classList.remove('is-calm');
      this.calmT = setTimeout(() => { if (!this.open) this.el.classList.add('is-calm'); }, ms || CALM_MS);
    }

    // ---- markers and cards ----
    show(s, pin, byKey) {
      if (!s) return;
      clearTimeout(this.leaveT);
      clearTimeout(this.enterT);
      if (this.open === s) { if (pin) this.pinned = true; return; }
      if (this.open) this.hide();
      this.open = s;
      this.pinned = !!pin;
      // phones / small tablets: the card is a fixed sheet; the kitchen is lifted above the sections after it while it
      // is open (.has-sheet: z-index 39, under the sticky header's 40). Nothing is moved in the page: an inserted node
      // would make the theme's body:has(...) rules restyle the whole page.
      if (sheet.matches) {
        s.card.classList.add('is-sheet');
        this.el.classList.add('has-sheet');
      }
      s.card.hidden = false;
      this.place(s);
      s.el.classList.add('is-open');
      s.btn.setAttribute('aria-expanded', 'true');
      this.light(s.app);
      this.hello(s.app);
      this.wake(CALM_MS);
      this.pingSync();
      if (byKey && s.card.classList.contains('is-sheet')) {
        const go = s.card.querySelector('.cek-card__go');
        if (go) go.focus({ preventScroll: true });
      }
    }

    hide() {
      const s = this.open;
      clearTimeout(this.enterT);
      if (!s) return;
      this.open = null;
      this.pinned = false;
      s.card.hidden = true;
      s.card.classList.remove('is-sheet');
      this.el.classList.remove('has-sheet');
      s.el.classList.remove('is-open');
      s.btn.setAttribute('aria-expanded', 'false');
      this.light('');
      this.pingSync();
    }

    preview(s) {
      if (!s || this.pinned || sheet.matches) return;
      clearTimeout(this.leaveT);
      clearTimeout(this.enterT);
      if (this.open === s) return;
      this.enterT = setTimeout(() => this.show(s, false), this.open ? 50 : 110);
    }

    leave(s) {
      clearTimeout(this.enterT);
      if (this.pinned || !s) return;
      clearTimeout(this.leaveT);
      this.leaveT = setTimeout(() => { if (this.open === s && !this.pinned) this.hide(); }, 240);
    }

    // the card stays inside the drawing's frame (computers); phones show it as a bottom sheet (CSS)
    place(s) {
      const c = s.card;
      c.style.setProperty('--dx', '0px');
      if (sheet.matches || !this.frame) return;
      const fr = this.frame.getBoundingClientRect();
      const cr = c.getBoundingClientRect();
      const pad = 6;
      let dx = 0;
      if (cr.left < fr.left + pad) dx = fr.left + pad - cr.left;
      else if (cr.right > fr.right - pad) dx = fr.right - pad - cr.right;
      if (dx) c.style.setProperty('--dx', Math.round(dx) + 'px');
    }

    light(app) {
      if (!this.art) return;
      this.el.classList.toggle('has-hot', !!app);
      this.stage.querySelectorAll('.cek-art .is-hot, .cek-fxo.is-hot').forEach((g) => g.classList.remove('is-hot'));
      this.spots.forEach((s) => s.el.classList.toggle('is-lit', !!app && s.app === app));
      if (app) this.stage.querySelectorAll('.cek-art [data-app="' + app + '"], .cek-art [data-life="' + app + '"], .cek-fxo[data-life="' + app + '"]').forEach((g) => g.classList.add('is-hot'));
    }

    // an appliance's own little moment (hood lifts, ice drops, the espresso machine puffs ...)
    hello(app) {
      if (!app || !this.art || reduce.matches || !this.el.classList.contains('is-done')) return;
      const now = Date.now();
      if (now - (this.helloAt[app] || 0) < 2200) return;
      this.helloAt[app] = now;
      const gs = this.stage.querySelectorAll('.cek-art [data-app="' + app + '"], .cek-art [data-life="' + app + '"], .cek-fxo[data-life="' + app + '"]');
      gs.forEach((g) => g.classList.remove('is-hello'));
      if (gs[0]) gs[0].getBoundingClientRect();
      gs.forEach((g) => g.classList.add('is-hello'));
      setTimeout(() => gs.forEach((g) => g.classList.remove('is-hello')), 2100);
    }

    // Tommy 12-10 ("would be cool if the ice maker was a little animated, made a few ice cubes every now and again,
    // also the coffee machine make some steam"): while the finished kitchen is on screen, the ice maker drops its
    // cubes and the espresso machine puffs steam by themselves now and again - each about every 9-14 s, out of step
    // with each other - using their own "hello" moments. Off with reduced motion, a hidden tab or off screen.
    idleSync() {
      const run = this.seen && this.el.classList.contains('is-done') && !reduce.matches && !D.hidden && !!this.art;
      if (run && !this.idleT) {
        this.idleT = {};
        const loop = (app, wait) => {
          this.idleT[app] = setTimeout(() => {
            if (!this.idleT) return;
            this.hello(app);
            loop(app, 9000 + Math.random() * 5000);
          }, wait);
        };
        loop('ice', 2500 + Math.random() * 2000);
        loop('espresso', 7000 + Math.random() * 3000);
      } else if (!run && this.idleT) {
        Object.values(this.idleT).forEach(clearTimeout);
        this.idleT = null;
      }
    }

    // one marker at a time sends out a slow ping while nothing is open
    pingSync() {
      this.idleSync();
      const run = this.seen && !this.open && this.el.classList.contains('is-done') && !reduce.matches && !D.hidden && this.spots.length > 1;
      if (run && !this.ping) {
        this.ping = setInterval(() => {
          const prev = this.spots[this.pingAt];
          if (prev) prev.el.classList.remove('is-ping');
          this.pingAt = (this.pingAt + 1) % this.spots.length;
          this.spots[this.pingAt].el.classList.add('is-ping');
        }, 1500);
      } else if (!run && this.ping) {
        clearInterval(this.ping);
        this.ping = 0;
        this.spots.forEach((s) => s.el.classList.remove('is-ping'));
      }
    }

    // computers: fetch the card photos in idle time once the show is over, so a card opens with its photo
    preload() {
      if (sheet.matches) return;
      idle(() => this.spots.forEach((s) => {
        const img = s.card.querySelector('img[loading="lazy"]');
        if (img) img.loading = 'eager';
      }));
    }

    bind() {
      this.spots.forEach((s) => {
        s.btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.open === s && this.pinned) this.hide();
          else this.show(s, true, e.detail === 0);
        });
        s.card.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && !s.card.classList.contains('is-sheet')) clearTimeout(this.leaveT); });
        s.card.addEventListener('focusout', (e) => {
          if (this.open === s && !this.pinned && !s.card.contains(e.relatedTarget) && !s.el.contains(e.relatedTarget)) this.hide();
        });
        if (s.x) s.x.addEventListener('click', () => { this.hide(); this.refocus(s); });
        s.el.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.preview(s); });
        s.el.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.leave(s); });
        s.btn.addEventListener('focus', () => {
          if (this.muting) return;                         // the focus handed back after a close: stay closed
          let kb = false;
          try { kb = s.btn.matches(':focus-visible'); } catch (err) { kb = false; }
          if (kb && this.open !== s) this.show(s, false);
        });
        s.el.addEventListener('focusout', (e) => {
          if (this.open === s && !this.pinned && !s.el.contains(e.relatedTarget) && !s.card.contains(e.relatedTarget)) this.hide();
        });
      });
      if (this.art) {
        const appOf = (t) => (t && t.closest ? t.closest('.cek-app[data-spot]') : null);
        this.art.addEventListener('pointerover', (e) => {
          if (e.pointerType !== 'mouse') return;
          const g = appOf(e.target);
          if (g) this.preview(this.byApp.get(g.getAttribute('data-app')));
        });
        this.art.addEventListener('pointerout', (e) => {
          if (e.pointerType !== 'mouse') return;
          const g = appOf(e.target);
          if (g && !g.contains(e.relatedTarget)) this.leave(this.byApp.get(g.getAttribute('data-app')));
        });
        this.art.addEventListener('click', (e) => {
          const g = appOf(e.target);
          if (!g) return;
          const s = this.byApp.get(g.getAttribute('data-app'));
          if (this.open === s && this.pinned) this.hide();
          else this.show(s, true);
        });
      }
      // pointing at the kitchen or swiping it wakes the loops (checked at most every 1.5 s)
      let lastWake = 0;
      const nudge = () => { const t = Date.now(); if (t - lastWake > 1500) { lastWake = t; this.wake(CALM_MS); } };
      if (this.frame) {
        this.frame.addEventListener('pointermove', nudge, { passive: true });
        const sc = this.frame.querySelector('.cek__scroll');
        if (sc) sc.addEventListener('scroll', nudge, { passive: true });
      }
      this.onKey = (e) => {
        if (e.key !== 'Escape' || !this.open) return;
        const s = this.open;
        this.hide();
        this.refocus(s);
      };
      this.onDown = (e) => {
        if (!this.open) return;
        const t = e.target;
        if (this.open.el.contains(t) || this.open.card.contains(t)) return;
        if (t && t.closest && t.closest('.cek-app[data-spot]') && this.el.contains(t)) return;
        this.hide();
      };
      D.addEventListener('keydown', this.onKey);
      D.addEventListener('pointerdown', this.onDown, true);
    }

    // Tommy 11-10, phones: the list under the kitchen shows its numbered cards first (CSS order); here they also go
    // first in the page so the tab order matches, and "Show all" opens the rest and moves the focus to the first of them
    shortList() {
      const list = this.el.querySelector('.cek__list--short');
      const more = this.el.querySelector('[data-cek-more]');
      if (!list || !more) return;
      const tops = Array.prototype.filter.call(list.children, (li) => li.hasAttribute('data-cek-top'))
        .sort((a, b) => (+a.getAttribute('data-cek-top')) - (+b.getAttribute('data-cek-top')));
      if (W.matchMedia('(max-width: 749px)').matches) tops.slice().reverse().forEach((li) => list.insertBefore(li, list.firstChild));
      more.addEventListener('click', () => {
        tops.slice().reverse().forEach((li) => list.insertBefore(li, list.firstChild));
        list.classList.remove('cek__list--short');
        more.setAttribute('aria-expanded', 'true');
        more.hidden = true;
        const next = list.children[tops.length] && list.children[tops.length].querySelector('a');
        if (next) next.focus({ preventScroll: true });
      });
    }

    // hand the focus back to a marker after its card was closed, without that focus opening it again
    refocus(s) {
      this.muting = true;                                 // focus events fire synchronously inside .focus()
      try { s.btn.focus({ preventScroll: true }); } finally { this.muting = false; }
    }

    destroy() {
      this.timers.forEach(clearTimeout);
      clearTimeout(this.calmT);
      clearInterval(this.ping);
      if (io && this.frame) io.unobserve(this.frame);
      D.removeEventListener('keydown', this.onKey);
      D.removeEventListener('pointerdown', this.onDown, true);
      kitchens.delete(this);
    }
  }

  const init = (root) => {
    (root || D).querySelectorAll('[data-cek]').forEach((el) => {
      if (el.ceKitchen) return;
      el.ceKitchen = new Kitchen(el);
      kitchens.add(el.ceKitchen);
    });
  };
  const find = (node) => {
    const el = node && node.closest ? node.closest('[data-cek]') : null;
    return el && el.ceKitchen;
  };

  D.addEventListener('visibilitychange', () => kitchens.forEach((k) => k.pingSync()));
  if (reduce.addEventListener) reduce.addEventListener('change', () => kitchens.forEach((k) => { if (reduce.matches) k.finish(); }));
  D.addEventListener('shopify:section:load', (e) => init(e.target));
  D.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-cek]').forEach((el) => { if (el.ceKitchen) el.ceKitchen.destroy(); el.ceKitchen = null; });
  });
  D.addEventListener('shopify:block:select', (e) => {
    const k = find(e.target);
    if (!k) return;
    const s = k.spots.find((x) => x.el === e.target || x.el.contains(e.target));
    k.finish();
    if (s) k.show(s, true);
  });
  D.addEventListener('shopify:block:deselect', (e) => { const k = find(e.target); if (k) k.hide(); });

  W.CEKitchen = { init, get: (el) => el && el.ceKitchen };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init()); else init();
})();
